import type { AxiosRequestConfig } from "axios";
import { api } from "@workspace/shared/core/services/api";
import libraryRoutes from "@workspace/shared/core/services/library/library.routes";
import {
  type CreateFolderPayload,
  type FolderDto,
  type LibraryGame,
  type LibraryPlay,
  type SavePlaysPayload,
  type SaveSettingsPayload,
  type UpdateFolderPayload,
} from "@workspace/shared/core/services/library/library.types";
import {
  formatLocalGameDate,
  gameDateForDay,
  todayLocalISO,
} from "@workspace/shared/core/utils/practiceDate";

// ---------- Folders ----------

const onGetFolders = () => api.get<FolderDto[]>(libraryRoutes.folderRoute);

const onCreateFolder = (payload: CreateFolderPayload) =>
  api.post<FolderDto>(libraryRoutes.folderRoute, payload);

const onUpdateFolder = (folderId: number, payload: UpdateFolderPayload) =>
  api.patch<FolderDto>(libraryRoutes.aFolderRoute(folderId), payload);

const onDeleteFolder = (folderId: number) =>
  api.delete(libraryRoutes.aFolderRoute(folderId));

const onMoveGameToFolder = (gameId: number, folderId: number | null) =>
  api.patch(libraryRoutes.moveGameToFolderRoute(gameId), { folder_id: folderId });

// ---------- Games ----------

const onGetGames = (config?: AxiosRequestConfig) =>
  api.get<LibraryGame[]>(libraryRoutes.gameRoute, config);

/** Bulk-persist Game Library sort positions after a drag reorder. */
const onReorderGames = (items: Array<{ game_id: number; position: number }>) =>
  api.patch(libraryRoutes.gamePositionsRoute, { items });

const onGetLatestGame = () => api.get<LibraryGame[]>(libraryRoutes.latestGameRoute);

const onGetGame = (gameId: number | string) =>
  api.get<LibraryGame>(libraryRoutes.aGameRoute(gameId));

/**
 * Uploads get longer than the client's 30 s default: an offline practice's
 * backlog goes up in ONE request, and on a weak sideline uplink it can take
 * more than 30 s — every retry then timed out the same way and it never synced
 * (audit 2026-10-19). The API's edge cuts at 120 s anyway. A caller's own
 * timeout still wins.
 */
const UPLOAD_TIMEOUT_MS = 120_000;

const onSaveGame = (payload: LibraryGame, config?: AxiosRequestConfig) => {
  // A queued save's confirmed day is set once it lands (onMoveGameToDay),
  // never sent with it.
  const game = { ...payload };
  delete game.confirmed_day;
  return api.post<LibraryGame>(libraryRoutes.gameRoute, game, {
    timeout: UPLOAD_TIMEOUT_MS,
    ...config,
  });
};

const onDeleteGame = (gameId: number) =>
  api.delete(libraryRoutes.aGameRoute(gameId));

const onRenameGame = (gameId: number, gameDescription: string) =>
  api.patch(libraryRoutes.renameGameRoute(gameId), {
    game_description: gameDescription,
  });

/** Move a saved game to another day (Game Library date picker). `gameDate` is
 *  an ISO timestamp — noon of the chosen local day, see gameDateForDay. */
const onChangeGameDate = (gameId: number, gameDate: string) =>
  api.patch(libraryRoutes.changeGameDateRoute(gameId), { game_date: gameDate });

/**
 * The day a save must still be moved to (client 2026-11-04): the one its
 * coach confirmed in Save Game, when the save didn't land on it — POST
 * /games stamps the moment it arrives. `savedAt` is the date the API gave
 * the game (now, if it didn't say). Null when nothing needs moving.
 */
const confirmedDayToKeep = (
  day: string | null | undefined,
  savedAt?: string | null,
): string | null => {
  if (!day || !gameDateForDay(day)) return null;
  return day === (formatLocalGameDate(savedAt) || todayLocalISO()) ? null : day;
};

/** Move a saved game to a day ("2026-11-08"): noon of it, as the Library's date picker does. */
const onMoveGameToDay = (gameId: number, day: string, config?: AxiosRequestConfig) => {
  const gameDate = gameDateForDay(day);
  if (!gameDate) return Promise.reject(new Error(`Not a day a game can move to: ${day}`));
  return api.patch(libraryRoutes.changeGameDateRoute(gameId), { game_date: gameDate }, config);
};

// ---------- Plays ----------

const onSavePlays = (payload: SavePlaysPayload, config?: AxiosRequestConfig) =>
  api.post<LibraryPlay[]>(libraryRoutes.playRoute, payload, {
    timeout: UPLOAD_TIMEOUT_MS,
    ...config,
  });

const onDeletePlay = (playId: string | number, config?: AxiosRequestConfig) => {
  const pathId = typeof playId === "number" ? playId : encodeURIComponent(playId);
  return api.delete(libraryRoutes.aPlayRoute(pathId), {
    ...config,
    data: { play_id: playId },
  });
};

const onClearCurrentGamePlays = () => api.delete(libraryRoutes.playRoute);

// ---------- Settings ----------

const onSaveSettings = (
  settings: SaveSettingsPayload,
  config?: AxiosRequestConfig,
) => api.post<SaveSettingsPayload>(libraryRoutes.settingsRoute, settings, config);

const onGetSettings = () => api.get<SaveSettingsPayload>(libraryRoutes.settingsRoute);

export default {
  // Folders
  onGetFolders,
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  onMoveGameToFolder,
  // Games
  onGetGames,
  onReorderGames,
  onGetLatestGame,
  onGetGame,
  onSaveGame,
  onDeleteGame,
  onRenameGame,
  onChangeGameDate,
  confirmedDayToKeep,
  onMoveGameToDay,
  // Plays
  onSavePlays,
  onDeletePlay,
  onClearCurrentGamePlays,
  // Settings
  onSaveSettings,
  onGetSettings,
};
